/**
 * Yield & Fee Reserve Screen
 *
 * Mirrors the Yield&Fee_Res_sales-based Excel sheet (table starting row 19).
 * Reserve covers servicing fees, senior expenses, stressed base rate, and
 * margin over the stressed DSO horizon. Per the guidance, this reserve is
 * purely dynamic — there is no floor.
 *
 *   F = avg_daily_gross_sales (rolling mean over DSO measurement period)
 *   G = total_net_ageing_balance (row-sum of net ageing per date)
 *   H = DSO = G / F
 *   I = servicing_fees + senior_expenses                  (broadcast scalar)
 *   J = ir_stress_multiple * base_rate                    (broadcast scalar)
 *   K = applicable_margin                                 (broadcast scalar)
 *   L = stressed_dso = dso_stress_multiple * H
 *   M = (I + J + K) * L / days_per_year                   (yield & fee reserve %)
 */
import { useState } from "react";
import { useDispatch } from "react-redux";
// -------------------------------------------------------------
import { calculateYield, getYieldData } from "Redux/data/actions";
import { useAnalysis, useScenarioFlag } from "Redux/data/hooks";
import DataTable from "Components/DataTable";
import SkeletonTable from "Components/Skeleton";
import CalcParamsModal, { ParamsButton } from "Components/CalcParamsModal";
import { YIELD_RESERVE_FIELDS } from "Config/calcParamsConfig";

const headers = [
  { text: "Reference Date", key: "Reference Date", datatype: "date" },
  { text: "Gross Sales", key: "gross_sales", datatype: "float" },
  { text: "Avg Daily Gross Sales over Period", key: "avg_daily_gross_sales", datatype: "float" },
  { text: "Total Net Ageing Balance", key: "total_net_ageing_balance", datatype: "float" },
  { text: "DSO", key: "dso", datatype: "float" },
  { text: "Servicing Fees + Senior Expenses", key: "servicing_and_senior_expenses", datatype: "float", display: "percentage" },
  { text: "Stressed Base Rate", key: "stressed_base_rate", datatype: "float", display: "percentage" },
  { text: "Applicable Margin", key: "applicable_margin", datatype: "float", display: "percentage" },
  { text: "Stressed DSO", key: "stressed_dso", datatype: "float" },
  { text: "Yield & Fee Reserve", key: "yield_fee_reserve", datatype: "float", display: "percentage" },
];

const YieldReserve = () => {
  const dispatch = useDispatch();
  const [calculating, setCalculating] = useState(false);
  const [showParams, setShowParams] = useState(false);

  const { data: yieldData, has: hasYield } = useAnalysis("yield", getYieldData);
  const hasMaster = useScenarioFlag("hasMaster");

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  return (
    <div>
      {showParams && (
        <CalcParamsModal
          section="yieldReserve"
          title="Yield & Fee Reserve Parameters"
          fields={YIELD_RESERVE_FIELDS}
          onClose={() => setShowParams(false)}
        />
      )}
      <div className="box" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <button
          className={`button is-small ${calculating ? "is-loading" : ""}`}
          onClick={() => {
            setCalculating(true);
            dispatch(calculateYield(() => setCalculating(false)));
          }}
        >
          {hasYield ? "Recalculate" : "Calculate"} Yield & Fee Reserve
        </button>
        <ParamsButton onClick={() => setShowParams(true)} />
      </div>
      {hasYield && !yieldData && <SkeletonTable />}
      {yieldData && (
        <DataTable
          data={yieldData.data}
          headers={headers}
          reference="Reference Date"
        />
      )}
    </div>
  );
};

export default YieldReserve;
